import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLocation } from "@/lib/location";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash, Upload, Download, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PhosphorIcon } from "@/components/PhosphorIcon";
import * as XLSX from "xlsx";
import { guessMenuIcon } from "@/lib/menuIcon";

/* ---- Tiny CSV helpers (used by Export). XLSX handles all import parsing. ---- */
function csvEscape(v) {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadBlob(filename, content) {
    const blob = new Blob([content], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function AdminMenu() {
    const { activeLocationId, requireLocation } = useLocation() || {};
    const [items, setItems] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({
        name: "", category: "Lunch", description: "", price: 100, half_price: "", quantity: "", icon: "ForkKnife", color: "#B45309", available: true,
    });
    // Edit dialog state — null when closed.
    const [editItem, setEditItem] = useState(null);
    const [editForm, setEditForm] = useState({
        name: "", category: "Lunch", description: "", price: 100, half_price: "", quantity: "", icon: "ForkKnife", color: "#B45309", available: true,
    });
    const [savingEdit, setSavingEdit] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        const { data } = await api.get("/menu");
        setItems(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeLocationId]);

    const create = async () => {
        if (!form.name) return toast.error("Name required");
        try {
            // Half price is optional — only send it if the admin typed a value.
            const half = form.half_price === "" ? null : parseFloat(form.half_price);
            await api.post("/menu", { ...form, price: parseFloat(form.price), half_price: half });
            toast.success("Item added");
            setShow(false);
            setForm({ name: "", category: "Lunch", description: "", price: 100, half_price: "", quantity: "", icon: "ForkKnife", color: "#B45309", available: true });
            load();
        } catch { toast.error("Failed"); }
    };

    const toggle = async (item) => {
        try { await api.patch(`/menu/${item.id}`, { available: !item.available }); load(); } catch { toast.error("Failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete item?")) return;
        try { await api.delete(`/menu/${id}`); load(); } catch { toast.error("Failed"); }
    };

    // Open the Edit dialog for a specific menu item — prefill the form with the
    // item's current values so the admin only changes what they want.
    const openEdit = (item) => {
        setEditItem(item);
        setEditForm({
            name: item.name || "",
            category: item.category || "Lunch",
            description: item.description || "",
            price: item.price ?? 0,
            half_price: item.half_price == null ? "" : String(item.half_price),
            quantity: item.quantity || "",
            icon: item.icon || "ForkKnife",
            color: item.color || "#B45309",
            available: item.available !== false,
        });
    };

    const saveEdit = async () => {
        if (!editItem) return;
        if (!editForm.name.trim()) { toast.error("Name required"); return; }
        setSavingEdit(true);
        try {
            // half_price: empty string → clear (null); otherwise parse to float.
            const half = editForm.half_price === "" ? null : parseFloat(editForm.half_price);
            const payload = {
                name: editForm.name.trim(),
                category: editForm.category.trim() || "Lunch",
                description: editForm.description.trim(),
                price: parseFloat(editForm.price) || 0,
                half_price: half,
                quantity: editForm.quantity.trim(),
                icon: editForm.icon.trim() || "ForkKnife",
                color: editForm.color || "#B45309",
                available: !!editForm.available,
            };
            await api.patch(`/menu/${editItem.id}`, payload);
            toast.success("Item updated");
            setEditItem(null);
            load();
        } catch {
            toast.error("Failed to update item");
        } finally {
            setSavingEdit(false);
        }
    };

    /* ---- Excel/CSV sync ---- */
    const downloadTemplate = () => {
        // Two supported formats — pick whichever your sheet uses:
        //   A. Single row per item with both `price` and `half_price` columns.
        //   B. Two rows per item where the name ends in "Full" / "Half"
        //      (or with a "portion" column saying Full / Half). Same item
        //      base names get merged automatically during import.
        // The template demonstrates both styles below.
        const csv = "name,portion,quantity,category,price,half_price,description,available\n"
            + "Veg Sandwich,,1 piece,Snacks,80,,Mixed veg with chutney,yes\n"
            + "Masala Chai,,150ml,Beverages,30,,Indian spiced tea,yes\n"
            + "Paneer Lunch Box,,1 plate,Lunch,180,110,Paneer butter masala + 2 roti,yes\n"
            + "Veg Biryani,Full,1 plate,Lunch,160,,Aromatic basmati rice,yes\n"
            + "Veg Biryani,Half,1/2 plate,Lunch,100,,Aromatic basmati rice,yes\n"
            + "Chicken Curry (Full),,1 plate,Lunch,220,,Spicy curry,yes\n"
            + "Chicken Curry (Half),,1/2 plate,Lunch,140,,Spicy curry,yes\n";
        downloadBlob("menu-import-template.csv", csv);
    };

    const exportCSV = () => {
        const rows = items.map((i) => ({
            name: i.name, quantity: i.quantity || "", category: i.category, price: i.price,
            half_price: i.half_price == null ? "" : i.half_price,
            description: i.description || "",
            available: i.available ? "yes" : "no",
        }));
        const head = "name,quantity,category,price,half_price,description,available\n";
        const body = rows.map((r) => [r.name, r.quantity, r.category, r.price, r.half_price, r.description, r.available].map(csvEscape).join(",")).join("\n");
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        downloadBlob(`menu-${stamp}.csv`, head + body);
        toast.success(`Exported ${items.length} items`);
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            // Parse either CSV or Excel (.xlsx/.xls) into a uniform row array.
            // SheetJS reads CSV too, so we route everything through it for
            // consistent column-name handling regardless of file format.
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: "array" });
            const firstSheet = wb.SheetNames[0];
            if (!firstSheet) { toast.error("Workbook has no sheets"); return; }
            const sheet = wb.Sheets[firstSheet];
            // header:1 → array of arrays; first row becomes our header map.
            const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false });
            if (!raw.length || raw.length < 2) {
                toast.error("File is empty or missing a header row");
                return;
            }
            const headers = raw[0].map((h) => String(h || "").trim().toLowerCase());
            const rows = raw.slice(1).map((arr) => {
                const obj = {};
                headers.forEach((h, i) => { obj[h] = arr[i] == null ? "" : String(arr[i]).trim(); });
                return obj;
            }).filter((r) => Object.values(r).some((v) => v !== ""));
            if (!rows.length) { toast.error("No data rows found"); return; }

            // Detect a Half / Full portion from either an explicit `portion`
            // column ("Full" / "Half" / "F" / "H") or from the item name itself
            // (e.g. "Veg Biryani (Half)", "Paneer Half", "Half Plate Biryani").
            // Returns { baseName, portion } where portion is "half" | "full" |
            // null. The baseName has the portion text stripped so that two
            // rows like "Veg Biryani Full" + "Veg Biryani Half" merge into
            // one item with half_price + price.
            const detectPortion = (rawName, rawPortion) => {
                let portion = null;
                const hint = String(rawPortion || "").trim().toLowerCase();
                if (["half", "h", "small", "s"].includes(hint)) portion = "half";
                else if (["full", "f", "regular", "large", "l"].includes(hint)) portion = "full";
                let name = String(rawName || "").trim();
                const halfPats = [/\s*\(\s*half\s*\)\s*$/i, /\s*[-–]\s*half\s*$/i, /\s+half\s*$/i, /^half\s+/i, /\s*\(\s*small\s*\)\s*$/i];
                const fullPats = [/\s*\(\s*full\s*\)\s*$/i, /\s*[-–]\s*full\s*$/i, /\s+full\s*$/i, /^full\s+/i, /\s*\(\s*large\s*\)\s*$/i, /\s*\(\s*regular\s*\)\s*$/i];
                if (!portion) { for (const p of halfPats) if (p.test(name)) { portion = "half"; break; } }
                if (!portion) { for (const p of fullPats) if (p.test(name)) { portion = "full"; break; } }
                halfPats.forEach((p) => { name = name.replace(p, ""); });
                fullPats.forEach((p) => { name = name.replace(p, ""); });
                return { baseName: name.trim(), portion };
            };

            // First pass — parse every row into a flat structure including the
            // detected portion. We merge across rows in a second pass below.
            const parsed = rows.map((r) => {
                const availStr = String(r.available || r["in_stock"] || r["in stock"] || "yes").toLowerCase();
                const rawName = (r.name || r["item name"] || r.item || "").trim();
                const category = (r.category || "Lunch").trim() || "Lunch";
                const { baseName, portion } = detectPortion(rawName, r.portion || r.size || r.type);
                const name = baseName || rawName;
                // Auto-pick an icon + tint from the item name's keywords.
                const { icon, color } = guessMenuIcon(name, category);
                // Robust price parsing — accept variants like "Price", "Cost",
                // "MRP", "Price (₹)", and tolerate currency symbols / commas /
                // textual prefixes ("Rs.", "INR.", "₹"). Strip commas first
                // then pull the first numeric token.
                const rawPrice = String(
                    r.price ?? r.cost ?? r.amount ?? r.mrp ?? r.rate ??
                    r["price (₹)"] ?? r["price (rs)"] ?? r["price(₹)"] ?? r["price (inr)"] ?? ""
                ).replace(/,/g, "");
                const priceMatch = rawPrice.match(/-?\d+(?:\.\d+)?/);
                const price = priceMatch ? parseFloat(priceMatch[0]) : 0;
                // half_price column (optional) takes precedence if present.
                const rawHalf = String(
                    r.half_price ?? r["half price"] ?? r["half"] ?? r["half_portion_price"] ?? ""
                ).replace(/,/g, "");
                const halfMatch = rawHalf.match(/-?\d+(?:\.\d+)?/);
                const half_price = halfMatch ? parseFloat(halfMatch[0]) : null;
                return {
                    name,
                    portion,                // "half" | "full" | null
                    quantity: String(r.quantity || r.qty || r["quantity (ml)"] || r["serving"] || "").trim(),
                    category,
                    price,
                    half_price,
                    description: (r.description || r["desc"] || "").trim(),
                    available: !["false", "no", "off", "0"].includes(availStr),
                    icon,
                    color,
                    location_id: activeLocationId || undefined,
                };
            });

            // Second pass — merge rows that share the same base name. If the
            // sheet has two rows for the same item ("Veg Biryani Full" +
            // "Veg Biryani Half") we collapse them into ONE menu item where
            // `price` comes from the Full row and `half_price` from the Half
            // row. This is exactly the user's "same food item with half +
            // full option" Excel format.
            const merged = new Map();
            let mergeCount = 0;
            for (const p of parsed) {
                const key = `${p.name.toLowerCase()}|${p.category.toLowerCase()}`;
                const existing = merged.get(key);
                if (!existing) {
                    merged.set(key, { ...p });
                    continue;
                }
                mergeCount += 1;
                // Decide which row contributes which price. Priority:
                // 1. Explicit portion column / suffix tells us directly.
                // 2. Otherwise the lower price is treated as "half".
                const incomingIsHalf = p.portion === "half" || (existing.portion === "full");
                const existingIsHalf = existing.portion === "half" || (p.portion === "full");
                if (incomingIsHalf) {
                    existing.half_price = p.price || existing.half_price;
                } else if (existingIsHalf) {
                    existing.half_price = existing.price;
                    existing.price = p.price;
                } else {
                    // Neither row was explicitly labelled — go by price.
                    if (p.price < existing.price) {
                        existing.half_price = p.price;
                    } else {
                        existing.half_price = existing.price;
                        existing.price = p.price;
                    }
                }
                // Fill blanks from the second row so we don't lose any data.
                if (!existing.description && p.description) existing.description = p.description;
                if (!existing.quantity && p.quantity) existing.quantity = p.quantity;
            }
            // Drop the temporary `portion` field — backend doesn't need it.
            const items = Array.from(merged.values()).map((r) => {
                // Strip the temporary `portion` marker before sending to backend.
                // eslint-disable-next-line no-unused-vars
                const { portion, ...rest } = r;
                return rest;
            });
            const confirmed = window.confirm(
                `Import ${items.length} menu item(s) from ${file.name}?\n\n` +
                (mergeCount > 0 ? `${mergeCount} half/full row pair(s) were merged into single menu items.\n\n` : "") +
                `Existing items with the same name in this location will be UPDATED (price / description / etc.).\n` +
                `New names will be CREATED.\n\n` +
                `Icons are auto-generated from item names.`
            );
            if (!confirmed) { fileRef.current.value = ""; return; }
            const { data } = await api.post("/menu/bulk-import", { items });
            const lines = [
                `Created: ${data.created_count}`,
                `Updated: ${data.updated_count}`,
                `Failed: ${data.failed_count}`,
            ];
            if (data.failed_count) {
                const reasons = (data.failed || []).slice(0, 3).map((f) => `• Row ${f.row}: ${f.reason}`).join("\n");
                toast.error(lines.join(" · "), { description: reasons });
            } else {
                toast.success(lines.join(" · "));
            }
            load();
        } catch (err) {
            toast.error("Import failed", { description: err?.message?.slice(0, 100) });
        } finally {
            fileRef.current.value = "";
        }
    };

    const grouped = items.reduce((acc, i) => { (acc[i.category] = acc[i.category] || []).push(i); return acc; }, {});

    return (
        <AppShell
            title="Cafeteria Menu"
            subtitle="Pre-order items"
            actions={
                <>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={handleImport}
                        className="hidden"
                        data-testid="menu-import-input"
                    />
                    <Button
                        variant="outline"
                        onClick={() => requireLocation && requireLocation(() => fileRef.current?.click(), "to import menu items")}
                        data-testid="menu-import-btn"
                    >
                        <Upload size={16} className="mr-1" /> Import Excel
                    </Button>
                    <Button variant="ghost" onClick={downloadTemplate} className="text-xs text-gray-600 dark:text-gray-400" data-testid="menu-template-btn">
                        Template
                    </Button>
                    <Button variant="outline" onClick={exportCSV} data-testid="menu-export-btn">
                        <Download size={16} className="mr-1" /> Export
                    </Button>
                    <Button
                        onClick={() => requireLocation && requireLocation(() => setShow(true), "a menu item")}
                        className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                        data-testid="menu-create-btn"
                    >
                        <Plus size={16} className="mr-1" /> New Item
                    </Button>
                    <Dialog open={show} onOpenChange={setShow}>
                        <DialogContent>
                            <DialogHeader><DialogTitle>New Menu Item</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="menu-name-input" /></div>
                                <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                                    <div><Label>Quantity (optional)</Label><Input value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 250ml, 1 plate" data-testid="menu-quantity-input" /></div>
                                    <div><Label>Full Price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="menu-price-input" /></div>
                                    <div>
                                        <Label>Half Price (₹) <span className="text-[10px] text-gray-500">optional</span></Label>
                                        <Input type="number" value={form.half_price} onChange={(e) => setForm({ ...form, half_price: e.target.value })} placeholder="leave blank if no half option" data-testid="menu-halfprice-input" />
                                    </div>
                                    <div><Label>Phosphor Icon</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></div>
                                    <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
                                </div>
                                <Button onClick={create} className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="menu-submit-btn">Add</Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
            }
        >
            <div className="p-6 space-y-8">
                {Object.entries(grouped).map(([cat, list]) => (
                    <section key={cat}>
                        <div className="label-eyebrow mb-3">{cat}</div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {list.map((m) => {
                                const hasHalf = m.half_price != null && Number(m.half_price) > 0;
                                return (
                                <Card key={m.id} className="bg-white dark:bg-[#111418] border-gray-200 dark:border-gray-800 p-4 flex items-center gap-3" data-testid={`menu-card-${m.id}`}>
                                    <div className="w-11 h-11 rounded-lg grid place-items-center" style={{ backgroundColor: `${m.color}15` }}>
                                        <PhosphorIcon name={m.icon} size={22} weight="duotone" color={m.color} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{m.name}</div>
                                        <div className="text-xs text-gray-500 truncate">{m.description}</div>
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                                            {hasHalf ? (
                                                <>
                                                    <span className="font-mono text-xs text-gray-700 dark:text-gray-300" data-testid={`menu-price-${m.id}`}>
                                                        Full ₹ {Number(m.price || 0).toFixed(0)}
                                                    </span>
                                                    <span className="font-mono text-xs text-gray-700 dark:text-gray-300" data-testid={`menu-halfprice-${m.id}`}>
                                                        · Half ₹ {Number(m.half_price).toFixed(0)}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="font-mono text-xs text-gray-700 dark:text-gray-300" data-testid={`menu-price-${m.id}`}>
                                                    ₹ {Number(m.price || 0).toFixed(0)}
                                                </span>
                                            )}
                                            {m.quantity ? (
                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300" data-testid={`menu-qty-${m.id}`}>
                                                    {m.quantity}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <Switch checked={m.available} onCheckedChange={() => toggle(m)} />
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(m)} className="text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800" data-testid={`menu-${m.id}-edit-btn`}>
                                        <PencilSimple size={14} />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => remove(m.id)} className="text-red-600 hover:bg-red-50" data-testid={`menu-${m.id}-delete-btn`}>
                                        <Trash size={14} />
                                    </Button>
                                </Card>
                                );
                            })}
                        </div>
                    </section>
                ))}
            </div>

            <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Edit Menu Item</DialogTitle></DialogHeader>
                    {editItem && (
                        <div className="space-y-3">
                            <div>
                                <Label>Name</Label>
                                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="menu-edit-name-input" />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} data-testid="menu-edit-description-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Category</Label>
                                    <Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} data-testid="menu-edit-category-input" />
                                </div>
                                <div>
                                    <Label>Quantity</Label>
                                    <Input value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} placeholder="e.g. 250ml, 1 plate" data-testid="menu-edit-quantity-input" />
                                </div>
                                <div>
                                    <Label>Full Price (₹)</Label>
                                    <Input type="number" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} data-testid="menu-edit-price-input" />
                                </div>
                                <div>
                                    <Label>Half Price (₹) <span className="text-[10px] text-gray-500">optional</span></Label>
                                    <Input type="number" value={editForm.half_price} onChange={(e) => setEditForm({ ...editForm, half_price: e.target.value })} placeholder="blank = no half option" data-testid="menu-edit-halfprice-input" />
                                </div>
                                <div>
                                    <Label>Phosphor Icon</Label>
                                    <Input value={editForm.icon} onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })} data-testid="menu-edit-icon-input" />
                                </div>
                                <div>
                                    <Label>Color</Label>
                                    <Input type="color" value={editForm.color} onChange={(e) => setEditForm({ ...editForm, color: e.target.value })} data-testid="menu-edit-color-input" />
                                </div>
                            </div>
                            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
                                <span className="text-sm text-gray-700 dark:text-gray-300">Available for ordering</span>
                                <Switch checked={editForm.available} onCheckedChange={(v) => setEditForm({ ...editForm, available: !!v })} data-testid="menu-edit-available-toggle" />
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                                <Button variant="outline" onClick={() => setEditItem(null)} className="flex-1" data-testid="menu-edit-cancel-btn">Cancel</Button>
                                <Button
                                    onClick={saveEdit}
                                    disabled={savingEdit}
                                    className="flex-1 bg-[#0055FF] hover:bg-[#0044CC] text-white"
                                    data-testid="menu-edit-save-btn"
                                >
                                    {savingEdit ? "Saving…" : "Save"}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
